import { useState } from 'react';
import { Hotel, Calendar, Users, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useTheme } from '@/context/ThemeContext';
import COUNTRIES from '@/data/countries';

const HotelServicePage = () => {
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    destination_country: '',
    destination_city: '',
    check_in_date: '',
    check_out_date: '',
    number_of_guests: '1',
    room_type: 'single',
    budget_range: '',
    special_requests: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/service-requests', {
        ...formData,
        service_category: 'hotel_booking',
        created_at: new Date().toISOString(),
      });
      
      toast.success('درخواست رزرو هتل شما ثبت شد. به زودی گزینه‌های مناسب را برای شما ارسال می‌کنیم.');
      
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        destination_country: '',
        destination_city: '',
        check_in_date: '',
        check_out_date: '',
        number_of_guests: '1',
        room_type: 'single',
        budget_range: '',
        special_requests: '',
      });
    } catch (error) {
      toast.error('خطا در ثبت درخواست.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const isDark = theme === 'dark';

  return (
    <div className={`min-h-screen py-12 ${isDark ? 'bg-black' : 'bg-gray-50'}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
            isDark ? 'bg-[#D4AF37]/10' : 'bg-[#D4AF37]/20'
          }`}>
            <Hotel className="w-10 h-10 text-[#D4AF37]" />
          </div>
          <h1 className={`text-4xl sm:text-5xl font-light mb-4 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>رزرو هتل</h1>
          <p className={`text-lg ${
            isDark ? 'text-zinc-400' : 'text-gray-600'
          }`}>رزرو هتل با بهترین قیمت و کیفیت</p>
        </div>

        <div className={`border rounded-2xl p-8 ${
          isDark 
            ? 'glass-effect border-white/10' 
            : 'bg-white border-gray-200 shadow-sm'
        }`}>
          <h2 className={`text-2xl font-light mb-6 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>فرم درخواست رزرو</h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm mb-2 ${
                  isDark ? 'text-zinc-300' : 'text-gray-700'
                }`}>نام و نام خانوادگی *</label>
                <input
                  type="text"
                  name="full_name"
                  required
                  value={formData.full_name}
                  onChange={handleChange}
                  className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none ${
                    isDark 
                      ? 'bg-white/5 border-white/10 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
              
              <div>
                <label className={`block text-sm mb-2 ${
                  isDark ? 'text-zinc-300' : 'text-gray-700'
                }`}>شماره تماس *</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none ${
                    isDark 
                      ? 'bg-white/5 border-white/10 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm mb-2 ${
                isDark ? 'text-zinc-300' : 'text-gray-700'
              }`}>ایمیل *</label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none ${
                  isDark 
                    ? 'bg-white/5 border-white/10 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm mb-2 ${
                  isDark ? 'text-zinc-300' : 'text-gray-700'
                }`}>کشور مقصد *</label>
                <select
                  name="destination_country"
                  required
                  value={formData.destination_country}
                  onChange={handleChange}
                  className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none font-medium ${
                    isDark 
                      ? 'bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] border-[#D4AF37]/30 text-[#D4AF37]' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>انتخاب کنید</option>
                  {COUNTRIES.map((country) => (
                    <option key={country} value={country} className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className={`block text-sm mb-2 ${
                  isDark ? 'text-zinc-300' : 'text-gray-700'
                }`}>شهر مقصد *</label>
                <input
                  type="text"
                  name="destination_city"
                  required
                  value={formData.destination_city}
                  onChange={handleChange}
                  className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none ${
                    isDark 
                      ? 'bg-white/5 border-white/10 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm mb-2 ${
                  isDark ? 'text-zinc-300' : 'text-gray-700'
                }`}>تاریخ ورود *</label>
                <input
                  type="date"
                  name="check_in_date"
                  required
                  value={formData.check_in_date}
                  onChange={handleChange}
                  className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none ${
                    isDark 
                      ? 'bg-white/5 border-white/10 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
              
              <div>
                <label className={`block text-sm mb-2 ${
                  isDark ? 'text-zinc-300' : 'text-gray-700'
                }`}>تاریخ خروج *</label>
                <input
                  type="date"
                  name="check_out_date"
                  required
                  value={formData.check_out_date}
                  onChange={handleChange}
                  className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none ${
                    isDark 
                      ? 'bg-white/5 border-white/10 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm mb-2 ${
                  isDark ? 'text-zinc-300' : 'text-gray-700'
                }`}>تعداد مهمان *</label>
                <select
                  name="number_of_guests"
                  value={formData.number_of_guests}
                  onChange={handleChange}
                  className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none font-medium ${
                    isDark 
                      ? 'bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] border-[#D4AF37]/30 text-[#D4AF37]' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="1" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>۱ نفر</option>
                  <option value="2" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>۲ نفر</option>
                  <option value="3" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>۳ نفر</option>
                  <option value="4" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>۴ نفر</option>
                  <option value="5+" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>بیشتر از ۴</option>
                </select>
              </div>
              
              <div>
                <label className={`block text-sm mb-2 ${
                  isDark ? 'text-zinc-300' : 'text-gray-700'
                }`}>نوع اتاق *</label>
                <select
                  name="room_type"
                  value={formData.room_type}
                  onChange={handleChange}
                  className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none font-medium ${
                    isDark 
                      ? 'bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] border-[#D4AF37]/30 text-[#D4AF37]' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="single" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>تک تخته</option>
                  <option value="double" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>دو تخته</option>
                  <option value="twin" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>توئین</option>
                  <option value="suite" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>سوئیت</option>
                </select>
              </div>
            </div>

            <div>
              <label className={`block text-sm mb-2 ${
                isDark ? 'text-zinc-300' : 'text-gray-700'
              }`}>بودجه تقریبی (به تومان)</label>
              <input
                type="text"
                name="budget_range"
                placeholder="مثلاً: 5,000,000 - 10,000,000 تومان در شب"
                value={formData.budget_range}
                onChange={handleChange}
                className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none ${
                  isDark 
                    ? 'bg-white/5 border-white/10 text-white placeholder-zinc-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
              />
            </div>

            <div>
              <label className={`block text-sm mb-2 ${
                isDark ? 'text-zinc-300' : 'text-gray-700'
              }`}>درخواست‌های ویژه</label>
              <textarea
                name="special_requests"
                rows="3"
                placeholder="مثلاً: نزدیک به مرکز شهر، صبحانه رایگان و..."
                value={formData.special_requests}
                onChange={handleChange}
                className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none ${
                  isDark 
                    ? 'bg-white/5 border-white/10 text-white placeholder-zinc-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#D4AF37] to-[#F4D03F] text-black font-medium py-4 rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
            >
              {loading ? 'در حال ارسال...' : 'ارسال درخواست رزرو'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default HotelServicePage;
